/**
 * Thin typed wrapper over the Telegram Mini App runtime (window.Telegram.WebApp,
 * loaded via telegram-web-app.js in index.html). Falls back gracefully when the
 * app runs outside Telegram during local development.
 */

interface TgBackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TgHaptics {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe?: { start_param?: string; [k: string]: unknown };
  version?: string;
  platform?: string;
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  /** Optional on older clients — the theme engine guards the calls. */
  onEvent?(event: string, cb: () => void): void;
  offEvent?(event: string, cb: () => void): void;
  /** Датчик ориентации Mini Apps (Bot API 8.0+); углы — В РАДИАНАХ. */
  DeviceOrientation?: {
    isStarted: boolean;
    alpha: number;
    beta: number;
    gamma: number;
    start(params?: { refresh_rate?: number; need_absolute?: boolean }, cb?: (started: boolean) => void): void;
    stop(cb?: () => void): void;
  };
  BackButton: TgBackButton;
  HapticFeedback: TgHaptics;
  /** Открывает Telegram-ссылку в самом клиенте; принимает ТОЛЬКО хост t.me. */
  openTelegramLink?(url: string): void;
  /** Оплата инвойса (Telegram Stars и т.п.) без ухода из Mini App. */
  openInvoice?(url: string, cb?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const tg: TgWebApp | undefined =
  typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

export const isTelegram = Boolean(tg && tg.initData);

/** Stars доступны только внутри Telegram и на клиентах с openInvoice. */
export const canPayStars = isTelegram && Boolean(tg?.openInvoice);

export function openInvoice(
  url: string,
  cb: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void,
): void {
  tg?.openInvoice?.(url, cb);
}

/**
 * Подписка на наклон устройства. В Telegram-webview стандартные
 * deviceorientation-события не приходят (iOS к тому же требует permission),
 * поэтому сначала пробуем Mini Apps API DeviceOrientation (Bot API 8.0+,
 * углы в радианах — приводим к градусам), fallback — обычные события.
 * Возвращает функцию отписки.
 */
export function onDeviceTilt(cb: (betaDeg: number, gammaDeg: number) => void): () => void {
  const d = tg?.DeviceOrientation;
  if (d && typeof d.start === 'function' && tg?.onEvent) {
    const handler = () => cb((d.beta * 180) / Math.PI, (d.gamma * 180) / Math.PI);
    tg.onEvent('deviceOrientationChanged', handler);
    d.start({ refresh_rate: 60 });
    return () => {
      tg?.offEvent?.('deviceOrientationChanged', handler);
      try {
        d.stop();
      } catch {
        /* старые клиенты */
      }
    };
  }
  const onTilt = (e: DeviceOrientationEvent) => {
    if (e.beta === null || e.gamma === null) return;
    cb(e.beta, e.gamma);
  };
  window.addEventListener('deviceorientation', onTilt);
  return () => window.removeEventListener('deviceorientation', onTilt);
}

/** Telegram client platform ('android'|'ios'|'tdesktop'|'web'…) and app version. */
export const tgPlatform: string = tg?.platform ?? 'web';
export const tgVersion: string = tg?.version ?? '';

/** Deep-link payload (?startapp=…) — for attribution. */
export function getStartParam(): string | null {
  return tg?.initDataUnsafe?.start_param ?? null;
}

/** Raw initData string to send to the API ('dev' triggers server bypass locally). */
export function getInitData(): string {
  return tg?.initData || 'dev';
}

export function initTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
  // Header/background colors are theme-dependent — set by theme.ts applyTheme().
}

/** Telegram-ссылка внутри Telegram; вне Telegram — обычная новая вкладка.
 *  Важно: telegram-web-app.js грузится и в обычном браузере, поэтому метод
 *  openTelegramLink существует всегда — но вне Telegram его вызов уходит в
 *  пустоту. Выбираем путь по isTelegram (живой initData), а не по наличию метода.
 *  Хосты: клиентский openTelegramLink принимает ТОЛЬКО t.me (ссылку обрабатывает
 *  сам Telegram, в сеть она не ходит — блокировка t.me ей не мешает); в браузере
 *  наоборот открываем через зеркало telegram.me, т.к. t.me блокируют провайдеры. */
export function openTelegramLink(url: string): void {
  if (isTelegram && tg?.openTelegramLink) {
    tg.openTelegramLink(url.replace('//telegram.me/', '//t.me/'));
  } else {
    window.open(url.replace('//t.me/', '//telegram.me/'), '_blank', 'noopener');
  }
}

export const haptics = {
  tap: () => tg?.HapticFeedback.selectionChanged(),
  success: () => tg?.HapticFeedback.notificationOccurred('success'),
  error: () => tg?.HapticFeedback.notificationOccurred('error'),
  impact: () => tg?.HapticFeedback.impactOccurred('medium'),
  // Открытие колоды (Straddle Pass 6.5): тап medium, легендарная heavy,
  // переворот карты light.
  light: () => tg?.HapticFeedback.impactOccurred('light'),
  medium: () => tg?.HapticFeedback.impactOccurred('medium'),
  heavy: () => tg?.HapticFeedback.impactOccurred('heavy'),
};

export const backButton = {
  show(cb: () => void) {
    if (!tg) return;
    tg.BackButton.onClick(cb);
    tg.BackButton.show();
  },
  hide(cb: () => void) {
    if (!tg) return;
    tg.BackButton.offClick(cb);
    tg.BackButton.hide();
  },
};
